import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import api from '../services/api';
import { useApiQuery, useApiMutation } from '../hooks/useApiQuery';
import useToast from '../hooks/useToast';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { Pencil, Trash2, ChevronDown, Check } from 'lucide-react';

/* ============================================================
   CUSTOM SELECT COMPONENT (smooth, animated dropdown)
   ============================================================ */
const CustomSelect = ({
  name,
  value,
  onChange,
  options = [],
  placeholder = 'Select an option',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (open) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  const handleSelect = (optionValue) => {
    // Mimic the native onChange event shape so existing handlers work
    onChange({ target: { name, value: optionValue } });
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`
          w-full flex items-center justify-between
          border rounded-[8px] px-3 py-2 text-sm text-left
          transition-all duration-200
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${
            open
              ? 'border-[#1672b9] ring-2 ring-[#1672b9]/20 bg-white'
              : 'border-[#dae1e7] bg-white hover:border-[#1672b9]/50'
          }
        `}
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`
            text-gray-400 transition-transform duration-200
            ${open ? 'rotate-180 text-[#1672b9]' : 'rotate-0'}
          `}
        />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="
            absolute z-50 mt-1 w-full
            bg-white rounded-[10px] border border-[#dae1e7]
            shadow-[0_8px_24px_rgba(3,0,71,0.12)]
            py-1 max-h-64 overflow-y-auto
            origin-top animate-dropdown
          "
        >
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">No options</div>
          ) : (
            options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSelect(opt.value)}
                  className={`
                    w-full flex items-center justify-between
                    px-3 py-2 text-sm text-left
                    transition-colors duration-150
                    ${
                      isSelected
                        ? 'bg-[#1672b9]/8 text-[#1672b9] font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  <span>{opt.label}</span>
                  {isSelected && (
                    <Check size={14} className="text-[#1672b9]" />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

/* ============================================================
   MAIN USER MANAGEMENT COMPONENT
   ============================================================ */
const UserManagement = () => {
  const { showSuccess, showError } = useToast();
  const { user: currentUser } = useAuth();
  const socket = useSocket();

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
  });
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // ✅ React Query: Fetch users
  const {data: rawUsers = [],isLoading: usersLoading,refetch: refetchUsers} = useApiQuery(
  ['users'],
  () => api.get('/auth/users').then((res) => res.data),
  {
    staleTime: 5 * 60 * 1000,
    onError: () => showError('Failed to load users'),
  },
);

// ✅ Hide event attendees (role = 'user') — this page manages staff only
const users = useMemo(
  () => rawUsers.filter((u) => u.role !== 'user'),
  [rawUsers],
);
  

  // ✅ React Query: Create user mutation
  const createMutation = useApiMutation(
    (data) => api.post('/auth/register', data).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccess('User created successfully!');
        setForm({ name: '', email: '', password: '', role: 'admin' });
        refetchUsers();
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Creation failed');
      },
    },
  );

  // ✅ React Query: Update user mutation
  const updateMutation = useApiMutation(
    ({ id, data }) =>
      api.put(`/auth/users/${id}`, data).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccess('User updated successfully!');
        setShowEditModal(false);
        setEditingUser(null);
        refetchUsers();
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Update failed');
      },
    },
  );

  // ✅ React Query: Delete user mutation
  const deleteMutation = useApiMutation(
    (id) => api.delete(`/auth/users/${id}`).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccess('User deleted successfully!');
        refetchUsers();
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Delete failed');
      },
    },
  );

  // ✅ Socket listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleUserChange = () => refetchUsers();

    socket.on('userCreated', handleUserChange);
    socket.on('userUpdated', handleUserChange);
    socket.on('userDeleted', handleUserChange);

    return () => {
      socket.off('userCreated', handleUserChange);
      socket.off('userUpdated', handleUserChange);
      socket.off('userDeleted', handleUserChange);
    };
  }, [socket, refetchUsers]);

  // ✅ Role options based on current user's role
  const roleOptions = useMemo(() => {
  if (currentUser?.role === 'super_admin') {
    return [
      // { value: 'user', label: 'User (Attendee)' },
      { value: 'event_editor', label: 'Event Editor' },
      { value: 'event_admin', label: 'Event Admin' },
      { value: 'device_scanner', label: 'Device Scanner' },
      { value: 'admin', label: 'Admin' },
      { value: 'super_admin', label: 'Super Admin' },
    ];
  } else if (currentUser?.role === 'admin') {
    return [
      // { value: 'user', label: 'User (Attendee)' },
      { value: 'event_editor', label: 'Event Editor' },
      { value: 'event_admin', label: 'Event Admin' },
      { value: 'device_scanner', label: 'Device Scanner' },
      { value: 'admin', label: 'Admin' },
      // ❌ No super_admin option for admin
    ];
  }
  return [];
}, [currentUser]);

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
    ],
    [],
  );

  // ✅ Handlers
  const handleChange = useCallback((e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      createMutation.mutate(form);
    },
    [form, createMutation],
  );

  const handleEdit = useCallback((user) => {
    setEditingUser(user);
    setForm({
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status,
      password: '',
    });
    setShowEditModal(true);
  }, []);

  const handleUpdate = useCallback(
    (e) => {
      e.preventDefault();
      const updateData = {
        name: form.name,
        email: form.email,
        role: form.role,
        status: form.status,
      };
      updateMutation.mutate({ id: editingUser._id, data: updateData });
    },
    [editingUser, form, updateMutation],
  );

  const handleDelete = useCallback(
    (userId, userName) => {
      if (
        !window.confirm(
          `Are you sure you want to delete user "${userName}"?`,
        )
      )
        return;
      deleteMutation.mutate(userId);
    },
    [deleteMutation],
  );

  const isSubmitting =
    createMutation.isPending || updateMutation.isPending;

  // ✅ Loading state
  if (usersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Animation keyframes for the dropdown */}
      <style>{`
        @keyframes dropdownIn {
          from {
            opacity: 0;
            transform: translateY(-4px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-dropdown {
          animation: dropdownIn 0.15s ease-out;
        }
      `}</style>

      <h1 className="text-2xl font-bold mb-6 text-[#2b3445]">
        User Management
      </h1>

      {/* Create User Form */}
      <div className="bg-white rounded-xl border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 text-[#2b3445]">
          Create New User
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={form.name}
              onChange={handleChange}
              className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
              required
              disabled={isSubmitting}
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={form.email}
              onChange={handleChange}
              className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
              required
              disabled={isSubmitting}
            />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
              required
              disabled={isSubmitting}
            />

            {/* ✅ Custom dropdown for role */}
            <CustomSelect
              name="role"
              value={form.role}
              onChange={handleChange}
              options={roleOptions}
              placeholder="Select role"
              disabled={isSubmitting}
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-[#1672b9] text-white px-4 py-2 rounded-[8px] font-medium hover:bg-[#0f5a9e] transition-all shadow-sm disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating...' : 'Create User'}
          </button>
        </form>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-xl border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] overflow-hidden">
        <h2 className="text-xl font-semibold p-4 border-b border-[#dae1e7] text-[#2b3445]">
          All Users
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="w-40 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-8 text-gray-500"
                  >
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm capitalize text-gray-700">
                      {user.role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 justify-center">
                        <button
                          onClick={() => handleEdit(user)}
                          disabled={isSubmitting}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                        >
                          <Pencil size={12} />
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            handleDelete(user._id, user.name)
                          }
                          disabled={deleteMutation.isPending}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition disabled:opacity-50"
                        >
                          {deleteMutation.isPending &&
                          deleteMutation.variables === user._id ? (
                            <span className="inline-block w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></span>
                          ) : (
                            <Trash2 size={12} />
                          )}
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto">
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h3 className="text-lg font-medium text-gray-900">
                Edit User
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                }}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-5 space-y-4">
              <div className="space-y-3">
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  required
                  disabled={isSubmitting}
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  required
                  disabled={isSubmitting}
                />

                {/* ✅ Custom dropdown for role */}
                <CustomSelect
                  name="role"
                  value={form.role}
                  onChange={handleChange}
                  options={roleOptions}
                  placeholder="Select role"
                  disabled={isSubmitting}
                />

                {/* ✅ Custom dropdown for status */}
                <CustomSelect
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  options={statusOptions}
                  placeholder="Select status"
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                  }}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-[8px] font-medium hover:bg-gray-300 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-[#1672b9] text-white rounded-[8px] font-medium hover:bg-[#0f5a9e] transition-all shadow-sm disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;